import { Tab as ChakraTab, TabProps as ChakraTabProps, useStyles } from '@chakra-ui/react';
import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useRef } from 'react';

import DisplayNames from './enums';

export type TabProps = ChakraTabProps & {
  truncated?: boolean;
};

const propTypes = {
  truncated: PropTypes.bool
};

const defaultProps = {
  truncated: false
};

const scrollElement = (element: HTMLElement) => {
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center'
  });
};

const Tab = forwardRef(
  (props: TabProps, ref: React.MutableRefObject<HTMLButtonElement>): React.ReactElement => {
    const newRef = useRef<HTMLButtonElement>(null);
    const componentRef = ref || newRef;
    const { onFocus, truncated, __css, ...restProps } = props;

    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLButtonElement, Element>) => {
        scrollElement(componentRef.current);
        if (onFocus) {
          onFocus(event);
        }
      },
      [componentRef, onFocus]
    );

    const themeStyles = useStyles();

    const overflow = truncated ? 'hidden' : undefined;
    const textOverflow = truncated ? 'ellipsis' : undefined;
    const whiteSpace = truncated ? 'nowrap' : undefined;
    const display = truncated ? 'block' : undefined;

    return (
      <ChakraTab
        {...restProps}
        __css={mergeWith(themeStyles, __css)}
        display={display}
        onFocus={handleFocus}
        overflow={overflow}
        ref={componentRef}
        textOverflow={textOverflow}
        whiteSpace={whiteSpace}
      />
    );
  }
);

Tab.displayName = DisplayNames.Tab;
Tab.propTypes = propTypes;
Tab.defaultProps = defaultProps;

export default Tab;
